import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { getStats } from "../api/stats.api";
import { getClientes } from "../api/admin.api";
import useAuth from "../hooks/useAuth";
import { ESTADOS_DIAGNOSTICO } from "../utils/constants";

import "../config.js";
import "./admin-report.css";

// Colour palette keyed by estado value, used across all chart types
const CHART_COLORS = {
  completado: "#10B981",
  en_progreso: "#001a72",
  en_revision: "#8B9DC5",
  pendiente: "#C5CAD5",
};

// Transforms the por_estado object ({ completado: 10, ... }) into Recharts-ready array
const transformEstadoDataForChart = (porEstado = {}) =>
  Object.entries(porEstado).map(([key, value]) => ({
    key,
    name: ESTADOS_DIAGNOSTICO[key]?.label ?? key,
    value,
    color: CHART_COLORS[key] ?? "#D1D5DB",
  }));

// Spanish short-month names used when formatting YYYY-MM strings
const MESES_ES = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

// Transforms por_mes array ([{ month: 'YYYY-MM', count: N, tiempo_medio?: N }]) into Recharts-ready array
const transformMesDataForChart = (porMes = []) =>
  porMes.map(({ month, count, tiempo_medio }) => {
    const [, m] = month.split("-");
    return {
      mes: MESES_ES[parseInt(m, 10) - 1],
      cantidad: count,
      tiempo_medio: tiempo_medio != null ? Number(tiempo_medio) : null,
    };
  });

// Returns { fecha_inicio, fecha_fin } for the last 12 months up to today in 'YYYY-MM-DD' format
const getDefaultDateRange = () => {
  const today = new Date();
  const yearAgo = new Date(today);
  yearAgo.setFullYear(today.getFullYear() - 1);

  // Format a Date object as 'YYYY-MM-DD'
  const fmt = (d) => d.toISOString().split("T")[0];

  return { fecha_inicio: fmt(yearAgo), fecha_fin: fmt(today) };
};

const formatDias = (value) => `${Number(value ?? 0).toFixed(1)} días`;

const AdminReport = (props) => {
  // Currently authenticated user (used for scoping if needed later)
  const { user, logout } = useAuth();

  // Stats payload returned by the API, null until first successful fetch
  const [stats, setStats] = useState(null);

  // True while the API request is in-flight
  const [loading, setLoading] = useState(true);

  // Stores an error message string when the fetch fails
  const [error, setError] = useState(null);

  // Active filter values; initialised with the last 12 months by default
  const [filters, setFilters] = useState({
    ...getDefaultDateRange(),
    cliente_id: "",
  });

  // List of clients available for the empresa filter (admin only)
  const [clientes, setClientes] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showInvestigacion, setShowInvestigacion] = useState(false);

  // Fetches the client list; only called when the current user is an admin
  const fetchClientes = async () => {
    try {
      const data = await getClientes({ page: 1, limit: 100 });
      setClientes(data.items ?? []);
    } catch {
      toast.error("Error al cargar la lista de empresas");
    }
  };

  // Updates a single filter value by name
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // Resets all filters back to the default date range and clears the client selection
  const clearFilters = () => {
    setFilters({ ...getDefaultDateRange(), cliente_id: "" });
  };

  // True when any filter differs from its default value
  const defaultRange = getDefaultDateRange();
  const hasActiveFilters =
    filters.fecha_inicio !== defaultRange.fecha_inicio ||
    filters.fecha_fin !== defaultRange.fecha_fin ||
    filters.cliente_id !== "";

  // Fetches stats from the API using the current filter values
  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      // Exclude empty-string values before sending to the API
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== ""),
      );
      const data = await getStats(activeFilters);
      setStats(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al cargar los informes";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Load client list on mount — only admins can filter by empresa
  useEffect(() => {
    if (user?.rol === "admin") fetchClientes();
  }, []);

  // Re-fetch whenever any filter value changes
  useEffect(() => {
    fetchStats();
  }, [filters.fecha_inicio, filters.fecha_fin, filters.cliente_id]);

  return (
    <div className="admin-report-container">
      <Helmet>
        <title>BIOLAB</title>
        <meta property="og:title" content="Informes - BIOLAB Platform" />
        <link rel="canonical" href="https://${BASE_URL}/admin-report" />
      </Helmet>
      <div className="window admin-report-thq-window-elm">
        <header data-thq="thq-navbar" className="admin-report-thq-navbar-elm">
          <div
            data-thq="thq-navbar-nav"
            className="admin-report-thq-desktop-menu-elm"
          >
            <div className="admin-report-thq-top-elm1">
              <div className="admin-report-thq-menus-elm1">
                <Link
                  to="/admin-analysis"
                  className="admin-report-thq-analysis-elm1 bodystandard"
                >
                  <img
                    alt="Análisis"
                    src="/menu icons/analysis.svg"
                    className="admin-report-thq-icon-elm10"
                  />
                  <span className="admin-report-thq-label-elm10 caption">
                    Análisis
                  </span>
                </Link>
                <button
                  type="button"
                  className="admin-report-thq-reports-elm1 bodystandard"
                >
                  <img
                    alt="Informes"
                    src="/menu icons/reports.svg"
                    className="admin-report-thq-icon-elm11"
                  />
                  <span className="admin-report-thq-label-elm11 caption">
                    Informes
                  </span>
                </button>
                <button
                  type="button"
                  className="admin-report-thq-admin-elm1 bodystandard"
                >
                  <img
                    alt="Gestión"
                    src="/menu icons/admin.svg"
                    className="admin-report-thq-icon-elm12"
                  />
                  <span className="admin-report-thq-label-elm12 caption">
                    Gestión
                  </span>
                </button>
              </div>
              <div className="admin-report-thq-submenu-elm1">
                <Link
                  to="/admin-clients"
                  className="admin-report-thq-clients-elm1 bodystandard"
                >
                  <img
                    alt="Clientes"
                    src="/menu icons/clients.svg"
                    className="admin-report-thq-icon-elm13"
                  />
                  <span className="admin-report-thq-label-elm13 caption">
                    Clientes
                  </span>
                </Link>
                <Link
                  to="/admin-access"
                  className="admin-report-thq-access-elm1 bodystandard"
                >
                  <img
                    alt="Accesos"
                    src="/menu icons/access.svg"
                    className="admin-report-thq-icon-elm14"
                  />
                  <span className="admin-report-thq-label-elm14 caption">
                    Accesos
                  </span>
                </Link>
              </div>
            </div>
            <button
              type="button"
              className="admin-report-thq-exit-elm1 bodystandard"
              onClick={logout}
            >
              <img
                alt="Salir"
                src="/menu icons/exit.svg"
                className="admin-report-thq-icon-elm15"
              />
              <span className="admin-report-thq-label-elm15 caption">
                Salir
              </span>
            </button>
          </div>
          <div
            data-thq="thq-burger-menu"
            className="admin-report-thq-burger-menu-elm"
            onClick={() => setIsMobileMenuOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsMobileMenuOpen(true);
              }
            }}
          >
            <svg viewBox="0 0 1024 1024" className="admin-report-icon1">
              <path d="M128 554.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 298.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667zM128 810.667h768c23.552 0 42.667-19.115 42.667-42.667s-19.115-42.667-42.667-42.667h-768c-23.552 0-42.667 19.115-42.667 42.667s19.115 42.667 42.667 42.667z"></path>
            </svg>
          </div>
          <div
            data-thq="thq-mobile-menu"
            className={`admin-report-thq-mobile-menu-elm${isMobileMenuOpen ? " admin-report-thq-mobile-menu-open" : ""}`}
          >
            <div className="admin-report-thq-nav-elm">
              <div className="admin-report-thq-content-elm1">
                <div className="admin-report-thq-header-elm">
                  <img
                    alt="BIOLAB Logo"
                    src="/biolab-logo-200h.png"
                    className="admin-report-thq-biolablogo-elm1"
                  />
                  <div
                    data-thq="thq-close-menu"
                    className="admin-report-thq-close-menu-elm"
                    onClick={closeMobileMenu}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        closeMobileMenu();
                      }
                    }}
                  >
                    <img
                      alt="image"
                      src="/close.svg"
                      className="admin-report-thq-icon-elm16"
                    />
                  </div>
                </div>
                <div className="admin-report-thq-top-elm2">
                  <div className="admin-report-thq-menus-elm2">
                    <Link
                      to="/admin-analysis"
                      className="admin-report-thq-analysis-elm2 bodystandard"
                      onClick={closeMobileMenu}
                    >
                      <img
                        alt="Análisis"
                        src="/menu icons/analysis.svg"
                        className="admin-report-thq-icon-elm17"
                      />
                      <span className="admin-report-thq-label-elm16">
                        Análisis
                      </span>
                    </Link>
                    <button
                      type="button"
                      className="admin-report-thq-reports-elm2 bodystandard"
                    >
                      <img
                        alt="Informes"
                        src="/menu icons/reports.svg"
                        className="admin-report-thq-icon-elm18"
                      />
                      <span className="admin-report-thq-label-elm17">
                        Informes
                      </span>
                    </button>
                    <button
                      type="button"
                      className="admin-report-thq-admin-elm2 bodystandard"
                    >
                      <img
                        alt="Gestión"
                        src="/menu icons/admin.svg"
                        className="admin-report-thq-icon-elm19"
                      />
                      <span className="admin-report-thq-label-elm18">
                        Gestión
                      </span>
                    </button>
                  </div>
                  <div className="admin-report-thq-submenu-elm2">
                    <Link
                      to="/admin-clients"
                      className="admin-report-thq-clients-elm2 bodystandard"
                      onClick={closeMobileMenu}
                    >
                      <img
                        alt="Clientes"
                        src="/menu icons/clients.svg"
                        className="admin-report-thq-icon-elm20"
                      />
                      <span className="admin-report-thq-label-elm19">
                        Clientes
                      </span>
                    </Link>
                    <Link
                      to="/admin-access"
                      className="admin-report-thq-access-elm2 bodystandard"
                      onClick={closeMobileMenu}
                    >
                      <img
                        alt="Accesos"
                        src="/menu icons/access.svg"
                        className="admin-report-thq-icon-elm21"
                      />
                      <span className="admin-report-thq-label-elm20">
                        Accesos
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="admin-report-thq-exit-elm2 bodystandard"
                onClick={logout}
              >
                <img
                  alt="Salir"
                  src="/menu icons/exit.svg"
                  className="admin-report-thq-icon-elm22"
                />
                <span className="admin-report-thq-label-elm21">Salir</span>
              </button>
            </div>
          </div>
        </header>
        <div className="admin-report-thq-content-elm2">
          <div className="admin-report-thq-title-elm1">
            <h1 className="admin-report-thq-title-elm2">Informes</h1>
            <img
              alt="BIOLAB Logo"
              src="/biolab-logo-200h.png"
              className="admin-report-thq-biolablogo-elm2"
            />
          </div>
          <div className="admin-report-thq-content-elm3">
            <div className="admin-report-modern-filter-section">
              {user?.rol === "admin" && (
                <div className="admin-report-modern-filters">
                  <label className="admin-report-filter-card filter-company">
                    <span className="admin-report-filter-label bodysmall">
                      Empresa
                    </span>
                    <select
                      value={filters.cliente_id}
                      onChange={(e) =>
                        handleFilterChange("cliente_id", e.target.value)
                      }
                      className="admin-report-filter-control bodysmall"
                    >
                      <option value="">Todas las empresas</option>
                      {clientes.map((cliente) => (
                        <option key={cliente.id} value={cliente.id}>
                          {cliente.nombre_empresa}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
              <div className="admin-report-modern-filters admin-report-modern-filters-secondary">
                <label className="admin-report-filter-card filter-start-date">
                  <span className="admin-report-filter-label bodysmall">
                    Fecha de inicio
                  </span>
                  <input
                    type="date"
                    value={filters.fecha_inicio}
                    onChange={(e) =>
                      handleFilterChange("fecha_inicio", e.target.value)
                    }
                    max={filters.fecha_fin || undefined}
                    className="admin-report-filter-control bodysmall"
                  />
                </label>
                <label className="admin-report-filter-card filter-end-date">
                  <span className="admin-report-filter-label bodysmall">
                    Fecha de fin
                  </span>
                  <input
                    type="date"
                    value={filters.fecha_fin}
                    onChange={(e) =>
                      handleFilterChange("fecha_fin", e.target.value)
                    }
                    min={filters.fecha_inicio || undefined}
                    className="admin-report-filter-control bodysmall"
                  />
                </label>
              </div>
            </div>
            <div className="admin-report-controls-row">
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="admin-report-clear-filters bodysmall"
                >
                  Limpiar filtros
                </button>
              )}
              <label className="admin-report-investigacion-toggle">
                <span className="bodysmall">
                  Habilitar análisis con investigaciones
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showInvestigacion}
                  className={`admin-report-toggle-switch${showInvestigacion ? " admin-report-toggle-switch--on" : ""}`}
                  onClick={() => setShowInvestigacion((v) => !v)}
                />
              </label>
            </div>
            <div className="admin-report-thq-charts-elm1">
              <div
                className={`admin-report-thq-kpis-elm${showInvestigacion ? " admin-report-thq-kpis-elm--5" : ""}`}
              >
                <div className="admin-report-thq-ongoing-elm">
                  <span className="admin-report-text19 headline02">
                    {loading ? "—" : error ? "!" : (stats?.kpis?.en_curso ?? 0)}
                  </span>
                  <span className="admin-report-text20 caption">
                    Análisis en curso
                  </span>
                </div>
                <div className="admin-report-thq-done-elm">
                  <span className="admin-report-text21 headline02">
                    {loading
                      ? "—"
                      : error
                        ? "!"
                        : (stats?.kpis?.pct_completados ?? 0) + "%"}
                  </span>
                  <span className="admin-report-text22 caption">
                    Análisis completados (%)
                  </span>
                </div>
                {showInvestigacion && (
                  <div className="admin-report-thq-ongoing-elm">
                    <span className="admin-report-text19 headline02">
                      {loading
                        ? "—"
                        : error
                          ? "!"
                          : (stats?.kpis?.pct_con_investigacion ?? 0) + "%"}
                    </span>
                    <span className="admin-report-text20 caption">
                      Análisis con investigación
                    </span>
                  </div>
                )}
                <div className="admin-report-thq-total-elm">
                  <span className="admin-report-text23 headline02">
                    {loading
                      ? "—"
                      : error
                        ? "!"
                        : (stats?.kpis?.total_analisis ?? 0)}
                  </span>
                  <span className="admin-report-text24 caption">
                    Total de análisis
                  </span>
                </div>
                <div className="admin-report-thq-time-elm">
                  <span className="admin-report-text25 headline02">
                    {loading
                      ? "—"
                      : error
                        ? "!"
                        : formatDias(stats?.kpis?.tiempo_medio_resolucion_dias)}
                  </span>
                  <span className="admin-report-text26">
                    Tiempo medio de resolución
                  </span>
                </div>
              </div>
              <div
                className={`admin-report-thq-charts-elm2${showInvestigacion ? " admin-report-thq-charts-elm2--with-inv" : ""}`}
              >
                {/* Chart 1 — donut breakdown of active diagnoses by estado */}
                <div className="admin-report-thq-ongoing-chart-elm">
                  <span className="admin-report-text27 subheadline">
                    Análisis en curso por estado
                  </span>
                  <div className="admin-report-thq-chart-elm1">
                    {loading ? (
                      <span>Cargando gráfico...</span>
                    ) : (
                      (() => {
                        const data = transformEstadoDataForChart(
                          stats?.charts?.por_estado,
                        );
                        return data.length === 0 ? (
                          <span>Sin datos</span>
                        ) : (
                          <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                              <Pie
                                data={data}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="44%"
                                innerRadius={70}
                                outerRadius={110}
                              >
                                {data.map((entry, index) => (
                                  <Cell key={index} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend
                                verticalAlign="bottom"
                                iconType="circle"
                                iconSize={10}
                                formatter={(value, entry) => (
                                  <span
                                    style={{ fontSize: 12, color: "#1a1919" }}
                                  >
                                    {value}{" "}
                                    <strong>{entry.payload.value}</strong>
                                  </span>
                                )}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        );
                      })()
                    )}
                  </div>
                </div>

                {/* Chart 2 — donut: analyses with/without investigation (only when toggle is on) */}
                {showInvestigacion &&
                  (() => {
                    const conInv = stats?.kpis?.con_investigacion ?? 0;
                    const total = stats?.kpis?.total_analisis ?? 0;
                    const sinInv = Math.max(0, total - conInv);
                    const invData = [
                      {
                        name: "Con investigación",
                        value: conInv,
                        color: "#001a72",
                      },
                      {
                        name: "Sin investigación",
                        value: sinInv,
                        color: "#C5CAD5",
                      },
                    ];
                    return (
                      <div className="admin-report-thq-ongoing-chart-elm">
                        <span className="admin-report-text27 subheadline">
                          Análisis con investigación
                        </span>
                        <div className="admin-report-thq-chart-elm1">
                          {loading ? (
                            <span>Cargando gráfico...</span>
                          ) : (
                            <ResponsiveContainer width="100%" height={280}>
                              <PieChart>
                                <Pie
                                  data={invData}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="44%"
                                  innerRadius={70}
                                  outerRadius={110}
                                >
                                  {invData.map((entry, index) => (
                                    <Cell key={index} fill={entry.color} />
                                  ))}
                                </Pie>
                                <Tooltip />
                                <Legend
                                  verticalAlign="bottom"
                                  iconType="circle"
                                  iconSize={10}
                                  formatter={(value, entry) => (
                                    <span
                                      style={{ fontSize: 12, color: "#1a1919" }}
                                    >
                                      {value}{" "}
                                      <strong>{entry.payload.value}</strong>
                                    </span>
                                  )}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                {/* Chart 3 — monthly bar+line combo: completed analyses + avg resolution time */}
                <div
                  className={`admin-report-thq-done-time-chart-elm${showInvestigacion ? " admin-report-thq-done-time-chart-elm--full" : ""}`}
                >
                  <span className="admin-report-text28 subheadline">
                    Análisis completados y tiempo medio
                  </span>
                  <div className="admin-report-thq-chart-elm2">
                    {loading ? (
                      <span>Cargando gráfico...</span>
                    ) : (
                      (() => {
                        const data = transformMesDataForChart(
                          stats?.charts?.por_mes,
                        );
                        const hasTiempo = data.some(
                          (d) => d.tiempo_medio != null,
                        );
                        return data.length === 0 ? (
                          <span>Sin datos</span>
                        ) : (
                          <ResponsiveContainer width="100%" height={300}>
                            <ComposedChart
                              data={data}
                              margin={{
                                top: 10,
                                right: hasTiempo ? 30 : 10,
                                left: 0,
                                bottom: 0,
                              }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#e8ecf4"
                              />
                              <XAxis
                                dataKey="mes"
                                tick={{ fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                yAxisId="left"
                                allowDecimals={false}
                                tick={{ fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              {hasTiempo && (
                                <YAxis
                                  yAxisId="right"
                                  orientation="right"
                                  tick={{ fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                              )}
                              <Tooltip />
                              <Legend verticalAlign="bottom" iconSize={10} />
                              <Bar
                                yAxisId="left"
                                dataKey="cantidad"
                                name="Análisis completados"
                                fill="#001a72"
                                radius={[4, 4, 0, 0]}
                              />
                              {hasTiempo && (
                                <Line
                                  yAxisId="right"
                                  type="monotone"
                                  dataKey="tiempo_medio"
                                  name="Tiempo medio (días)"
                                  stroke="#F97316"
                                  strokeWidth={2}
                                  dot={{ r: 4, fill: "#F97316" }}
                                  activeDot={{ r: 6 }}
                                />
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        );
                      })()
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReport;
